import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowUp, ArrowDown, X, Info, Plus } from 'lucide-react';
import { toast } from 'sonner';

interface Project {
  id: string;
  title: string;
  location: string;
  status: string;
  images: Array<{ image_url: string; alt_text: string }>;
}

interface PageProject {
  id: string;
  project_id: string;
  display_order: number;
  project: Project;
}

interface Service {
  id: string;
  name: string;
  slug: string;
}

interface CityGroup {
  id: string;
  primary_city: string;
  region: string;
}

const PageProjectsManager = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [cityGroups, setCityGroups] = useState<CityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Page selection
  const [pageType, setPageType] = useState<'homepage' | 'service' | 'city'>('homepage');
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  
  // Featured and gallery projects
  const [featuredProjects, setFeaturedProjects] = useState<PageProject[]>([]);
  const [galleryProjects, setGalleryProjects] = useState<PageProject[]>([]);
  const [selectedProjectForFeatured, setSelectedProjectForFeatured] = useState('');
  const [selectedProjectForGallery, setSelectedProjectForGallery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (pageType) {
      loadPageProjects();
    }
  }, [pageType, selectedService, selectedCity]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsResult, servicesResult, citiesResult] = await Promise.all([
        supabase
          .from('projects')
          .select('*, images:project_images(*)')
          .eq('status', 'published')
          .order('created_at', { ascending: false }),
        supabase
          .from('services')
          .select('id, name, slug')
          .order('name'),
        supabase
          .from('city_groups')
          .select('id, primary_city, region')
          .order('primary_city')
      ]);

      if (projectsResult.data) setProjects(projectsResult.data);
      if (servicesResult.data) setServices(servicesResult.data);
      if (citiesResult.data) setCityGroups(citiesResult.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPageProjects = async () => {
    try {
      const featuredQuery = supabase
        .from('page_featured_projects')
        .select('*, project:projects(*, images:project_images(*))')
        .eq('page_type', pageType)
        .eq('is_active', true)
        .order('display_order');

      const galleryQuery = supabase
        .from('page_gallery_projects')
        .select('*, project:projects(*, images:project_images(*))')
        .eq('page_type', pageType)
        .eq('is_active', true)
        .order('display_order');

      if (pageType === 'service' && selectedService) {
        featuredQuery.eq('service_slug', selectedService);
        galleryQuery.eq('service_slug', selectedService);
      }
      if (pageType === 'city' && selectedCity) {
        featuredQuery.eq('city_name', selectedCity);
        galleryQuery.eq('city_name', selectedCity);
      }

      const [featuredResult, galleryResult] = await Promise.all([
        featuredQuery,
        galleryQuery
      ]);

      setFeaturedProjects(featuredResult.data || []);
      setGalleryProjects(galleryResult.data || []);
    } catch (error) {
      console.error('Error loading page projects:', error);
      toast.error('Failed to load page projects');
    }
  };

  const addFeaturedProject = async () => {
    if (!selectedProjectForFeatured) return;
    
    if (featuredProjects.length >= 3) {
      toast.error('Maximum 3 featured projects allowed');
      return;
    }

    try {
      const newOrder = Math.max(...featuredProjects.map(p => p.display_order), -1) + 1;
      
      const { error } = await supabase
        .from('page_featured_projects')
        .insert({
          project_id: selectedProjectForFeatured,
          page_type: pageType,
          service_slug: pageType === 'service' ? selectedService : null,
          city_name: pageType === 'city' ? selectedCity : null,
          display_order: newOrder
        });

      if (error) throw error;

      toast.success('Featured project added');
      setSelectedProjectForFeatured('');
      loadPageProjects();
    } catch (error) {
      console.error('Error adding featured project:', error);
      toast.error('Failed to add featured project');
    }
  };

  const addGalleryProject = async () => {
    if (!selectedProjectForGallery) return;
    
    if (galleryProjects.length >= 6) {
      toast.error('Maximum 6 gallery projects allowed');
      return;
    }

    try {
      const newOrder = Math.max(...galleryProjects.map(p => p.display_order), -1) + 1;
      
      const { error } = await supabase
        .from('page_gallery_projects')
        .insert({
          project_id: selectedProjectForGallery,
          page_type: pageType,
          service_slug: pageType === 'service' ? selectedService : null,
          city_name: pageType === 'city' ? selectedCity : null,
          display_order: newOrder
        });

      if (error) throw error;

      toast.success('Gallery project added');
      setSelectedProjectForGallery('');
      loadPageProjects();
    } catch (error) {
      console.error('Error adding gallery project:', error);
      toast.error('Failed to add gallery project');
    }
  };

  const removeProject = async (id: string, type: 'featured' | 'gallery') => {
    try {
      const table = type === 'featured' ? 'page_featured_projects' : 'page_gallery_projects';
      const { error } = await supabase.from(table).delete().eq('id', id);
      
      if (error) throw error;
      
      toast.success(`${type === 'featured' ? 'Featured' : 'Gallery'} project removed`);
      loadPageProjects();
    } catch (error) {
      console.error('Error removing project:', error);
      toast.error('Failed to remove project');
    }
  };

  const moveProject = async (id: string, direction: 'up' | 'down', type: 'featured' | 'gallery') => {
    const list = type === 'featured' ? featuredProjects : galleryProjects;
    const currentIndex = list.findIndex(p => p.id === id);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (swapIndex < 0 || swapIndex >= list.length) return;

    try {
      const table = type === 'featured' ? 'page_featured_projects' : 'page_gallery_projects';
      const current = list[currentIndex];
      const swap = list[swapIndex];

      await Promise.all([
        supabase.from(table).update({ display_order: swap.display_order }).eq('id', current.id),
        supabase.from(table).update({ display_order: current.display_order }).eq('id', swap.id)
      ]);

      loadPageProjects();
    } catch (error) {
      console.error('Error reordering projects:', error);
      toast.error('Failed to reorder projects');
    }
  };

  const getPageIdentifier = () => {
    if (pageType === 'homepage') return 'Homepage';
    if (pageType === 'service' && selectedService) {
      const service = services.find(s => s.slug === selectedService);
      return service ? `${service.name} Service Page` : 'Service Page';
    }
    if (pageType === 'city' && selectedCity) {
      const city = cityGroups.find(c => c.primary_city === selectedCity);
      return city ? `${city.primary_city} City Page` : 'City Page';
    }
    return 'Select a page';
  };

  const availableFeaturedProjects = projects.filter(
    p => !featuredProjects.some(fp => fp.project_id === p.id)
  );

  const availableGalleryProjects = projects.filter(
    p => !galleryProjects.some(gp => gp.project_id === p.id)
  );

  const isPageValid = () => {
    if (pageType === 'homepage') return true;
    if (pageType === 'service') return !!selectedService;
    if (pageType === 'city') return !!selectedCity;
    return false;
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Page Project Manager</CardTitle>
          <CardDescription>
            Select a page and manage which projects appear as featured (top 3) and in the gallery (up to 6)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium mb-2 block">Page Type</label>
              <Select value={pageType} onValueChange={(value: any) => setPageType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="homepage">Homepage</SelectItem>
                  <SelectItem value="service">Service Page</SelectItem>
                  <SelectItem value="city">City Page</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {pageType === 'service' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Select Service</label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose service..." />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.slug}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {pageType === 'city' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Select City</label>
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose city..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cityGroups.map(city => (
                      <SelectItem key={city.id} value={city.primary_city}>
                        {city.primary_city} ({city.region})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Currently managing: <strong>{getPageIdentifier()}</strong>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {isPageValid() && (
        <Tabs defaultValue="featured" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="featured">
              Featured Projects ({featuredProjects.length}/3)
            </TabsTrigger>
            <TabsTrigger value="gallery">
              Gallery Projects ({galleryProjects.length}/6)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="featured" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Add Featured Project</CardTitle>
                <CardDescription>Select up to 3 projects to feature prominently</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Select 
                    value={selectedProjectForFeatured} 
                    onValueChange={setSelectedProjectForFeatured}
                    disabled={featuredProjects.length >= 3}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Choose project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFeaturedProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.title} - {project.location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={addFeaturedProject} 
                    disabled={!selectedProjectForFeatured || featuredProjects.length >= 3}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>

                <div className="space-y-2">
                  {featuredProjects.map((fp, index) => (
                    <div key={fp.id} className="flex items-center gap-2 p-3 border rounded-lg">
                      <img 
                        src={fp.project.images[0]?.image_url} 
                        alt={fp.project.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{fp.project.title}</div>
                        <div className="text-sm text-muted-foreground">{fp.project.location}</div>
                      </div>
                      <Badge>Position {index + 1}</Badge>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveProject(fp.id, 'up', 'featured')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveProject(fp.id, 'down', 'featured')}
                          disabled={index === featuredProjects.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeProject(fp.id, 'featured')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {featuredProjects.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No featured projects added yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="gallery" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Add Gallery Project</CardTitle>
                <CardDescription>Select up to 6 projects for the gallery section</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Select 
                    value={selectedProjectForGallery} 
                    onValueChange={setSelectedProjectForGallery}
                    disabled={galleryProjects.length >= 6}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Choose project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableGalleryProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.title} - {project.location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={addGalleryProject} 
                    disabled={!selectedProjectForGallery || galleryProjects.length >= 6}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {galleryProjects.map((gp, index) => (
                    <div key={gp.id} className="flex items-center gap-2 p-3 border rounded-lg">
                      <img 
                        src={gp.project.images[0]?.image_url} 
                        alt={gp.project.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{gp.project.title}</div>
                        <div className="text-sm text-muted-foreground truncate">{gp.project.location}</div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveProject(gp.id, 'up', 'gallery')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => moveProject(gp.id, 'down', 'gallery')}
                          disabled={index === galleryProjects.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeProject(gp.id, 'gallery')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {galleryProjects.length === 0 && (
                    <p className="text-center text-muted-foreground py-8 col-span-2">
                      No gallery projects added yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default PageProjectsManager;
